import { OG_IMAGE_SRC, PRODUCT_NAME } from '../../config/product'
import { initMailRuCounter, trackMailRuReachGoal } from '../../utils/mailruCounter'
import { initYandexMetrika, trackYandexMetrikaGoal } from '../../utils/yandexMetrika'

const UTM_STORAGE_KEY = 'kartin_utm'
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const

export type LandingPageId = 'landing' | 'designers'

export function getSiteUrl(path = '/'): string {
  const fromEnv = import.meta.env.VITE_SITE_URL?.trim()
  const base = fromEnv ? fromEnv.replace(/\/$/, '') : (
    typeof window !== 'undefined' ? window.location.origin : 'https://spectrai.com'
  )
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${base}${normalized === '/' ? '/' : normalized}`
}

export function setMetaTag(key: string, value: string, useProperty = false) {
  const selector = useProperty ? `meta[property="${key}"]` : `meta[name="${key}"]`
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null
  if (!tag) {
    tag = document.createElement('meta')
    if (useProperty) {
      tag.setAttribute('property', key)
    } else {
      tag.setAttribute('name', key)
    }
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', value)
}

export function setCanonical(url: string) {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    document.head.appendChild(link)
  }
  link.setAttribute('href', url)
}

export interface LandingSeoConfig {
  page: LandingPageId
  title: string
  description: string
  path: string
}

export function getOgImageUrl(): string {
  return getSiteUrl(OG_IMAGE_SRC)
}

export function applyLandingSeo(config: LandingSeoConfig) {
  const url = getSiteUrl(config.path)
  const imageUrl = getOgImageUrl()
  const imageAlt = `${PRODUCT_NAME} — AI platform for business`
  document.title = config.title
  setMetaTag('description', config.description)
  setMetaTag('og:title', config.title, true)
  setMetaTag('og:description', config.description, true)
  setMetaTag('og:type', 'website', true)
  setMetaTag('og:url', url, true)
  setMetaTag('og:site_name', PRODUCT_NAME, true)
  setMetaTag('og:locale', 'en_US', true)
  setMetaTag('og:image', imageUrl, true)
  setMetaTag('og:image:width', '1200', true)
  setMetaTag('og:image:height', '630', true)
  setMetaTag('og:image:alt', imageAlt, true)
  setMetaTag('twitter:card', 'summary_large_image')
  setMetaTag('twitter:title', config.title)
  setMetaTag('twitter:description', config.description)
  setMetaTag('twitter:image', imageUrl)
  setCanonical(url)
}

function designersGoalName(source: string): string {
  if (source === 'form_submit') return 'designers_form_submit'
  if (['hero_secondary', 'final_cta', 'contact_after_form'].includes(source)) {
    return 'designers_cta_register'
  }
  return 'designers_cta_apply'
}

export function trackLandingCta(page: LandingPageId, source: string) {
  const eventName = page === 'designers' ? designersGoalName(source) : 'landing_cta_click'
  const payload = {
    event: eventName,
    source,
    page,
  }

  ;(window as any).dataLayer?.push(payload)
  ;(window as any).gtag?.('event', eventName, payload)
  trackMailRuReachGoal(page === 'designers' ? eventName : `landing_cta_${source}`)
  trackYandexMetrikaGoal(eventName, { source, page })
}

export function initLandingAnalytics() {
  initMailRuCounter()
  initYandexMetrika()
}

export function useLandingDarkThemeEffect() {
  return () => {
    const root = document.documentElement
    const prevTheme = root.getAttribute('data-theme')
    root.setAttribute('data-theme', 'dark')

    return () => {
      if (prevTheme === 'light' || prevTheme === 'dark') {
        root.setAttribute('data-theme', prevTheme)
      } else {
        const fromStorage = localStorage.getItem('theme')
        if (fromStorage === 'light' || fromStorage === 'dark') {
          root.setAttribute('data-theme', fromStorage)
        } else {
          root.removeAttribute('data-theme')
        }
      }
    }
  }
}

export function captureUtmParams() {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)
  const utm: Record<string, string> = {}
  for (const key of UTM_PARAMS) {
    const value = params.get(key)
    if (value) utm[key] = value
  }
  if (Object.keys(utm).length > 0) {
    try {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm))
    } catch {
      /* ignore */
    }
  }
}

export function buildRegisterPath(from: string): string {
  const query = new URLSearchParams({ from })
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY)
    if (raw) {
      const utm = JSON.parse(raw) as Record<string, string>
      for (const [key, value] of Object.entries(utm)) {
        if (value) query.set(key, value)
      }
    }
  } catch {
    /* ignore */
  }
  return `/register?${query.toString()}`
}

const LEAD_EMAIL = 'inbox@spectrai.pro'

export function buildLeadMailto(subject: string, body: string): string {
  return `mailto:${LEAD_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

export function openLeadChannel(subject: string, body: string) {
  window.location.href = buildLeadMailto(subject, body)
}
